/* @jsxImportSource react */
'use client';

import React from 'react';

import { useRender } from '@base-ui/react/use-render';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import { useObservabilitySafe } from '../../helpers/useObservabilitySafe';
import { useAccessibleNameWarning } from '../../utils/accessible-name';
import { cn } from '../../utils/cn';
import { type LinkRenderProps, useLinkRenderer } from '../../utils/link-provider';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/** Button variant definitions mapping size and variant names to their Tailwind classes. */
export const ATOMS_BUTTON_VARIANTS = {
  size: {
    sm: {
      classes: 'h-8 gap-1 rounded-xl px-4 text-label-md',
      description: 'Small button for secondary actions',
    },
    md: {
      classes: 'h-11 gap-1 rounded-2xl px-5 text-label-md',
      description: 'Medium button for compact contexts',
    },
    lg: {
      classes: 'h-14 gap-1.5 rounded-2xl px-6 text-label-lg',
      description: 'Large button (default)',
    },
  },
  variant: {
    primary: {
      classes:
        'border border-od-border-accent-brand bg-od-bg-accent-brand !text-od-content-inverse-primary not-disabled:hover:border-od-border-hover-accent-brand not-disabled:hover:bg-od-bg-hover-accent-brand not-disabled:active:border-od-border-pressed-accent-brand not-disabled:active:bg-od-bg-pressed-accent-brand disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-accent-brand',
      description: 'High-emphasis button for primary actions',
    },
    secondary: {
      classes:
        'border border-od-border-primary bg-od-bg-primary !text-od-content-primary not-disabled:hover:border-od-border-hover-secondary not-disabled:hover:bg-od-bg-hover-primary not-disabled:active:border-od-border-hover-primary not-disabled:active:bg-od-bg-hover-primary disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-secondary data-[state=open]:bg-od-bg-primary',
      description: 'Default button style for most actions',
    },
    secondaryInverse: {
      classes:
        'border border-transparent bg-od-bg-inverse-secondary !text-od-content-inverse-primary not-disabled:hover:border-od-border-inverse-hover-secondary not-disabled:hover:bg-od-bg-inverse-hover-secondary not-disabled:active:border-od-border-inverse-pressed-secondary not-disabled:active:bg-od-bg-inverse-pressed-secondary disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-secondary',
      description: 'Secondary button for use on inverted/dark surfaces',
    },
    destructive: {
      classes:
        'border border-od-border-error bg-od-bg-error !text-od-content-inverse-primary not-disabled:hover:border-od-border-hover-accent-error not-disabled:hover:bg-od-bg-hover-accent-error not-disabled:active:border-od-border-pressed-accent-error not-disabled:active:bg-od-bg-pressed-accent-error disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-od-bg-inverse-inactive-primary disabled:not-aria-busy:!text-od-content-tertiary focus-visible:outline-od-border-focused-accent-error',
      description: 'High-emphasis red button for destructive actions',
    },
  },
} as const;

export const ATOMS_BUTTON_DEFAULT_VARIANTS = {
  size: 'lg',
  variant: 'primary',
} as const;

export type AtomsButtonSize = keyof typeof ATOMS_BUTTON_VARIANTS.size;
export type AtomsButtonVariant = keyof typeof ATOMS_BUTTON_VARIANTS.variant;

export interface AtomsButtonVariantsProps {
  /**
   * Button size.
   * - `"sm"` — Small for secondary actions
   * - `"md"` — Medium for compact contexts
   * - `"lg"` — Large (default)
   * @default "lg"
   */
  size?: AtomsButtonSize;
  /**
   * Visual style of the button.
   * - `"primary"` — High-emphasis, brand-colored for primary actions
   * - `"secondary"` — Style with border for most actions
   * - `"secondaryInverse"` — Secondary style for inverted/dark surfaces
   * - `"destructive"` — High-emphasis red for destructive actions (delete, remove)
   * @default "primary"
   */
  variant?: AtomsButtonVariant;
}

export function buttonVariants({
  variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant,
  size = ATOMS_BUTTON_DEFAULT_VARIANTS.size,
}: AtomsButtonVariantsProps = {}) {
  return cn(
    'group flex w-max shrink-0 items-center justify-center select-none',
    'transition-transform not-disabled:active:scale-95',
    'focus:outline-none focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-offset-2',
    'cursor-pointer',
    // Disabled text color is set per-variant below (with `!important`), which
    // shadows any base `disabled:text-*`, so none is set here.
    'disabled:cursor-not-allowed',
    ATOMS_BUTTON_VARIANTS.variant[variant].classes,
    ATOMS_BUTTON_VARIANTS.size[size].classes,
  );
}

/** Props shared by both the button and link forms of {@link Button}. */
type ButtonSharedProps = AtomsButtonVariantsProps &
  AnalyticsProps & {
    /**
     * Explicit accessible name — always required, even with text children, so
     * the announced name is stable and reviewable rather than derived from
     * whatever happens to render inside.
     */
    'aria-label': string;
    /** Content rendered inside the button. Compose icons via children. */
    children?: React.ReactNode;
    /** Additional CSS classes merged via `cn()`. */
    className?: string;
  };

/**
 * Button props for the default form — renders a native `<button>` (no `href`).
 *
 * @example
 * ```tsx
 * <Button aria-label="Save">Save</Button>
 * <Button aria-label="Save" variant="secondary" loading>Saving…</Button>
 * ```
 */
export type ButtonAsButtonProps = StripUndesiredProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> &
  ButtonSharedProps & {
    /** Omitted on the button form; pass `href` to render an anchor instead. */
    href?: never;
    /** Shows a loading indicator and disables interaction. */
    loading?: boolean;
  };

/**
 * Button props for the link form — renders an anchor (`<a>`) styled as a button,
 * selected by passing `href`. Integrates with `LinkProvider` for framework routing.
 *
 * @example
 * ```tsx
 * <Button aria-label="Docs" href="/docs" variant="secondary">Docs</Button>
 * <Button aria-label="Visit Site" href="https://example.com" external>Visit Site</Button>
 * ```
 */
export type ButtonAsLinkProps = StripUndesiredProps<React.AnchorHTMLAttributes<HTMLAnchorElement>> &
  ButtonSharedProps & {
    /** Navigation target. Its presence renders an anchor rather than a button. */
    href: string;
    /** When `true`, opens in a new tab with `rel="noopener noreferrer"`. */
    external?: boolean;
    /** Not supported on the link form. */
    loading?: never;
  };

/**
 * Button props — a discriminated union on `href`:
 * - without `href` → native `<button>` ({@link ButtonAsButtonProps})
 * - with `href` → anchor styled as a button ({@link ButtonAsLinkProps})
 */
export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const BUTTON_LOADER_DOT_CLASS: Record<AtomsButtonSize, string> = {
  sm: 'size-1.25',
  md: 'size-1.5',
  lg: 'size-1.75',
};

const BUTTON_LOADER_GAP_CLASS: Record<AtomsButtonSize, string> = {
  sm: 'gap-1',
  md: 'gap-1',
  lg: 'gap-1',
};

const BUTTON_LOADER_MIN_CLASS: Record<AtomsButtonVariant, string> = {
  primary: '[--atoms-button-loader-min:0.4]',
  secondary: '[--atoms-button-loader-min:0.3]',
  secondaryInverse: '[--atoms-button-loader-min:0.4]',
  destructive: '[--atoms-button-loader-min:0.4]',
};

const BUTTON_LOADER_DOT_ANIMATIONS = [
  'animate-[atoms-button-loader-1_1900ms_ease-in-out_infinite]',
  'animate-[atoms-button-loader-2_1900ms_ease-in-out_infinite]',
  'animate-[atoms-button-loader-3_1900ms_ease-in-out_infinite]',
  'animate-[atoms-button-loader-4_1900ms_ease-in-out_infinite]',
];

function ButtonLoadingDots({
  size,
  variant,
}: {
  size: AtomsButtonSize;
  variant: AtomsButtonVariant;
}) {
  return (
    <span
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- span as status is standard for loaders
      role="status"
      aria-label="Loading"
      className={cn(
        'absolute inset-0 flex items-center justify-center',
        BUTTON_LOADER_GAP_CLASS[size],
        BUTTON_LOADER_MIN_CLASS[variant],
      )}
    >
      {BUTTON_LOADER_DOT_ANIMATIONS.map((animClass) => (
        <span
          key={animClass}
          className={cn('rounded-full bg-current', BUTTON_LOADER_DOT_CLASS[size], animClass)}
        />
      ))}
    </span>
  );
}

// Both internal forms carry the public union element type (not their own narrower
// element) so the `Button` dispatcher can forward `ref` to either without a
// narrowing assertion. The ref still receives the concrete element at runtime.
type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

/**
 * Button form — renders a native `<button>`. Selected by {@link Button} when no
 * `href` is given. Owns the `loading` affordance (the only form that supports it).
 */
const ButtonAsButton = React.forwardRef<ButtonElement, ButtonAsButtonProps>(
  function ButtonAsButton(props, ref) {
    const {
      children,
      className,
      disabled,
      loading,
      type,
      size = ATOMS_BUTTON_DEFAULT_VARIANTS.size,
      variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant,
      analyticsName,
      trackImpression,
      onClick,
      ...rest
    } = props;

    const { trackJourneyEvent } = useObservabilitySafe();
    const viewRef = useImpressionRef<ButtonElement>(
      Boolean(analyticsName && trackImpression),
      analyticsName,
      'buttonViewEvent',
      ref,
    );

    // Backstop for the required `aria-label` (JS consumers / `as any` escapes).
    useAccessibleNameWarning('Button', Boolean(props['aria-label']));

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (analyticsName) {
          trackJourneyEvent('platform', 'buttonClickEvent', { targetName: analyticsName });
        }
        onClick?.(event);
      },
      [analyticsName, onClick, trackJourneyEvent],
    );

    return (
      // eslint-disable-next-line atoms/no-raw-elements -- this is the Button component's own <button> implementation
      <button
        ref={viewRef}
        type={type ?? 'button'}
        // Loading is a busy state that must block *every* activation path —
        // pointer, keyboard, and implicit form submission. Only the native
        // `disabled` attribute covers all three; `aria-disabled`/`pointer-events`
        // are ignored by keyboard activation and form submit. `aria-busy` both
        // announces the in-progress state to assistive tech AND gates the greyed
        // `disabled:not-aria-busy:*` variant styles, so a loading button keeps its
        // active colors (spinner over full color) rather than looking inactive.
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size }), 'relative', className)}
        onClick={handleClick}
        {...rest}
      >
        {loading && <ButtonLoadingDots size={size} variant={variant} />}
        {/* Children stay rendered but invisible during loading so the button
            keeps its natural width (driven by label/icon content) while the
            absolutely-positioned dot loader overlays them. `display: contents`
            makes the icon/text behave as direct flex items of the button, so the
            button's gap applies between them. */}
        <span aria-hidden={loading || undefined} className={cn('contents', loading && 'invisible')}>
          {children}
        </span>
      </button>
    );
  },
);

ButtonAsButton.displayName = 'Button';

/**
 * Link form — renders an anchor styled as a button, routed through
 * `LinkProvider`. Selected by {@link Button} when `href` is given.
 */
const ButtonAsLink = React.forwardRef<ButtonElement, ButtonAsLinkProps>(
  function ButtonAsLink(props, ref) {
    // `disabled` isn't on the link form's public type, but spread / loosely-typed
    // callers can still pass it. Read it through one localized escape so it's
    // translated to aria-disabled below and never lands on the <a>.
    const {
      children,
      className,
      external,
      href,
      disabled,
      size = ATOMS_BUTTON_DEFAULT_VARIANTS.size,
      variant = ATOMS_BUTTON_DEFAULT_VARIANTS.variant,
      analyticsName,
      trackImpression,
      onClick,
      ...rest
    } = props as ButtonAsLinkProps & { disabled?: boolean };

    const renderLink = useLinkRenderer();
    const { trackJourneyEvent } = useObservabilitySafe();

    // Backstop for the required `aria-label` (JS consumers / `as any` escapes).
    useAccessibleNameWarning('Button', Boolean(props['aria-label']));
    const viewRef = useImpressionRef<ButtonElement>(
      Boolean(analyticsName && trackImpression),
      analyticsName,
      'linkViewEvent',
      ref,
    );

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (analyticsName) {
          trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
        }
        onClick?.(event);
      },
      [analyticsName, onClick, trackJourneyEvent],
    );

    // Anchors have no native `disabled`; make a "disabled" link inert rather
    // than leaving it silently navigable.
    const linkDisabled = Boolean(disabled);

    // `satisfies` (not an annotation) keeps the inferred object-literal type,
    // which carries the implicit index signature `useRender`'s `props` wants,
    // while still type-checking the shape against the renderer's contract.
    const elementProps = {
      className: cn(
        buttonVariants({ variant, size }),
        'flex items-center no-underline!',
        linkDisabled && 'pointer-events-none',
        className,
      ),
      href: linkDisabled ? undefined : href,
      to: linkDisabled || typeof href !== 'string' ? undefined : href,
      'aria-disabled': linkDisabled || undefined,
      onClick: handleClick,
      ...rest,
      // `external` enforces a safe rel/target; keep noopener/noreferrer even if
      // the caller passed their own `rel`, so it can't be silently dropped.
      ...(external
        ? {
            target: '_blank',
            rel: [rest.rel, 'noopener', 'noreferrer'].filter(Boolean).join(' '),
          }
        : {}),
      children,
    } satisfies LinkRenderProps;

    // The link element type comes from the app's LinkProvider (e.g. NextLink),
    // so render via the configured renderer rather than a static <a>.
    return useRender({
      render: renderLink,
      defaultTagName: 'a',
      ref: viewRef,
      props: elementProps,
    });
  },
);

ButtonAsLink.displayName = 'Button';

/**
 * Primary action trigger. Renders a native `<button>` by default; pass `href` to
 * render an anchor styled identically (routed through `LinkProvider`). Supports
 * multiple variants and sizes, plus a `loading` state on the button form.
 * Compose icons by placing them inside `children`.
 *
 * @example
 * ```tsx
 * <Button aria-label="Save">Save</Button>
 * <Button aria-label="New listing" variant="secondary"><IconPlus />New listing</Button>
 * <Button aria-label="Settings" href="/settings" variant="secondary">Settings</Button>
 * <Button aria-label="Visit Site" href="https://example.com" external>Visit Site</Button>
 * ```
 */
export const Button = React.forwardRef<ButtonElement, ButtonProps>(function Button(props, ref) {
  // Dispatch on `href`: with it, the anchor form; without, the button form.
  // Each form is its own component with correctly-typed props and a single
  // concrete event type — no shared non-discriminated view or prop cast.
  if (props.href != null) {
    return <ButtonAsLink ref={ref} {...props} />;
  }
  return <ButtonAsButton ref={ref} {...props} />;
});

Button.displayName = 'Button';
